import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Container, Row, Col, Card, Button, Table, Badge, Alert } from "react-bootstrap";
import { useHistory } from "react-router-dom";
import { Redirect } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faPlus, faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import styles from "./UserManual.module.scss";

export function UserManualList() {
  const history = useHistory();
  const user = useSelector((state) => state.authentication?.user);
  const isAdmin = !!(user?.is_admin || user?.is_admin_or_qa);
  const [manuals, setManuals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isAdmin) {
      loadManuals();
    }
  }, [isAdmin]);

  const loadManuals = async () => {
    setLoading(true);
    setError(null);
    try {
      const auth = JSON.parse(localStorage.getItem('auth') || '{}');
      const token = auth.access_token;
      const baseUrl = process.env.REACT_APP_BASE_URL || '';
      const response = await axios.get(
        `${baseUrl}/api/user_manual`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.data.success && response.data.data) {
        setManuals(response.data.data);
      }
    } catch (err) {
      setError(err.response?.data?.message || "Error loading user manual sections");
      console.error("Error loading manuals:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    history.push('/user-manual/create');
  };

  const handleEdit = (id) => {
    history.push(`/user-manual/edit/${id}`);
  };

  const toggleActive = async (id, currentStatus) => {
    try {
      const auth = JSON.parse(localStorage.getItem('auth') || '{}');
      const token = auth.access_token;
      const baseUrl = process.env.REACT_APP_BASE_URL || '';
      await axios.post(
        `${baseUrl}/api/user_manual/${id}`,
        { is_active: !currentStatus },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      loadManuals();
    } catch (err) {
      setError(err.response?.data?.message || "Error updating status");
      console.error("Error toggling active status:", err);
    }
  };

  // Admin-only access guard (after hooks are declared)
  if (!isAdmin) {
    return <Redirect to="/" />;
  }

  if (loading) {
    return (
      <Container fluid className={styles['manual-container']}>
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Loading...</span>
          </div>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className={styles['manual-container']}>
      <Row>
        <Col>
          <Card className={styles['manual-card']}>
            <Card.Header className={styles['card-header']}>
              <div className="d-flex justify-content-between align-items-center">
                <h3 className="mb-0">User Manual Sections</h3>
                <Button variant="primary" onClick={handleCreate}>
                  {/* <FontAwesomeIcon icon={faPlus} className="me-2" /> */}
                  Create New Section
                </Button>
              </div>
            </Card.Header>
            <Card.Body className={styles['card-body']}>
              {error && (
                <Alert variant="danger" onClose={() => setError(null)} dismissible>
                  {error}
                </Alert>
              )}

              {manuals.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  <p>No user manual sections found.</p>
                  <Button variant="primary" onClick={handleCreate}>
                    Create First Section
                  </Button>
                </div>
              ) : (
                <Table striped bordered hover responsive>
                  <thead>
                    <tr>
                      <th style={{ width: '50px' }}>Order</th>
                      <th>Tab Name</th>
                      <th>Content (preview)</th>
                      <th style={{ width: '100px' }}>Status</th>
                      <th style={{ width: '150px' }}>Last Updated</th>
                      <th style={{ width: '150px' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {manuals.map((manual) => {
                      const stripHtml = (html) => {
                        if (!html) return '';
                        const tmp = document.createElement('div');
                        tmp.innerHTML = html;
                        const text = tmp.textContent || tmp.innerText || '';
                        return text.replace(/\s+/g, ' ').trim();
                      };
                      const snippet = (() => {
                        const text = stripHtml(manual.content);
                        return text.length > 200 ? text.slice(0, 200) + '…' : text;
                      })();
                      return (
                      <tr key={manual.id}>
                        <td className="text-center">{manual.sort_index}</td>
                        <td>
                          <strong>{manual.tab_name}</strong>
                        </td>
                        <td>{snippet}</td>
                        <td className="text-center text-white">
                          <Badge bg={manual.is_active ? 'success' : 'secondary'}>
                            {manual.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </td>
                        <td>
                          {new Date(manual.updated_at).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </td>
                        <td>
                          <div className="d-flex gap-2">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => handleEdit(manual.id)}
                              title="Edit"
                            >
                              <FontAwesomeIcon icon={faEdit} />
                            </Button>
                            &nbsp;
                            <Button
                              variant={manual.is_active ? 'outline-warning' : 'outline-success'}
                              size="sm"
                              onClick={() => toggleActive(manual.id, manual.is_active)}
                              title={manual.is_active ? 'Deactivate' : 'Activate'}
                            >
                              <FontAwesomeIcon icon={manual.is_active ? faEyeSlash : faEye} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

export default UserManualList;

